
import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, Check, ChevronDown, Calendar, Hash, Tag, DollarSign } from 'lucide-react';
import { FilterState, PropertyType } from '../types';

interface FiltersProps {
  filters: FilterState;
  setFilters: (f: FilterState) => void;
  isGridView: boolean;
  setIsGridView: (v: boolean) => void;
}

export const Filters: React.FC<FiltersProps> = ({ filters, setFilters }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const bedroomOptions = ['Any', '1', '2', '3', '4', '5', '6+'];
  const timeframeOptions = [
    { value: 'all', label: 'Anytime' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' }
  ];

  const toggleStatus = (key: 'is_furnished' | 'is_serviced' | 'is_shared') => {
    setFilters({ ...filters, [key]: !filters[key] });
  };

  const handleReset = () => {
    setFilters({
      keyword: '',
      ref: '',
      type: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      status: '',
      bedrooms: 'Any',
      is_furnished: false,
      is_serviced: false,
      is_shared: false,
      timeframe: 'all'
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Search Input Area */}
      <div className="flex items-center gap-2">
        <div className="relative group flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-zinc-600 transition-colors group-focus-within:text-black dark:group-focus-within:text-white" />
          <input 
            type="text"
            placeholder="Search by title or location..."
            className="w-full pl-11 pr-4 py-2.5 bg-gray-100 dark:bg-zinc-900 dark:text-white rounded-xl text-xs font-medium focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-zinc-700 transition-all placeholder:text-gray-400 dark:placeholder:text-zinc-600"
            value={filters.keyword}
            onChange={e => setFilters({...filters, keyword: e.target.value})}
          />
        </div>
      </div>

      {/* Quick Access Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button 
          onClick={() => setIsDrawerOpen(true)}
          className="flex-shrink-0 bg-gray-100 dark:bg-zinc-900 p-2.5 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-800 transition-all active:scale-90"
          title="Advanced Filter"
        >
          <SlidersHorizontal className="w-4 h-4 text-gray-900 dark:text-white" />
        </button>
        
        <div className="relative">
          <select 
            className="appearance-none pl-3 pr-8 py-2.5 bg-gray-100 dark:bg-zinc-900 dark:text-white rounded-lg text-[10px] font-black uppercase tracking-widest outline-none border-none cursor-pointer hover:bg-gray-200 dark:hover:bg-zinc-800 transition-colors"
            value={filters.category}
            onChange={e => setFilters({...filters, category: e.target.value})}
          >
            <option value="">Category</option>
            <option value="Sale">Buy</option>
            <option value="Rent">Rent</option>
            <option value="Short Let">Shortlet</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        </div>

        <div className="relative">
          <select 
            className="appearance-none pl-3 pr-8 py-2.5 bg-gray-100 dark:bg-zinc-900 dark:text-white rounded-lg text-[10px] font-black uppercase tracking-widest outline-none border-none cursor-pointer hover:bg-gray-200 dark:hover:bg-zinc-800 transition-colors"
            value={filters.type}
            onChange={e => setFilters({...filters, type: e.target.value})}
          >
            <option value="">All Types</option>
            <option value="Apartment">Apartment</option>
            <option value="House">House</option>
            <option value="Flat">Flat</option>
            <option value="Villa">Villa</option>
            <option value="Land">Land</option>
            <option value="Commercial">Commercial</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Advanced Search Drawer */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-[200] flex justify-end animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsDrawerOpen(false)} />
          
          <div className="relative w-full max-w-md bg-white dark:bg-zinc-950 h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500">
            <div className="p-6 border-b border-gray-100 dark:border-zinc-900 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight uppercase">Advanced Search</h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Refine your property hunt</p>
              </div>
              <button 
                onClick={() => setIsDrawerOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-900 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-hide">
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-50 dark:border-zinc-900 pb-2">
                  <Tag className="w-3.5 h-3.5 text-gray-400" />
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-900 dark:text-white">Search Tools</h3>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Availability Status</label>
                    <div className="relative">
                      <select 
                        className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm appearance-none border border-transparent focus:border-gray-200 dark:focus:border-zinc-800"
                        value={filters.status}
                        onChange={e => setFilters({...filters, status: e.target.value})}
                      >
                        <option value="">Any Status</option>
                        <option value="Available">Available</option>
                        <option value="Sold">Sold</option>
                        <option value="Reserved">Reserved</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Property Ref #</label>
                    <div className="relative">
                      <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input 
                        type="text" 
                        placeholder="Ref code"
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-xl text-xs font-bold outline-none border border-transparent focus:border-gray-200 dark:focus:border-zinc-800"
                        value={filters.ref}
                        onChange={e => setFilters({...filters, ref: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-400">Bedrooms</h3>
                <div className="flex flex-wrap gap-2">
                  {bedroomOptions.map(opt => (
                    <button
                      key={opt}
                      onClick={() => setFilters({ ...filters, bedrooms: opt })}
                      className={`px-4 py-2.5 rounded-full text-[11px] font-black transition-all active:scale-95 border-2 ${
                        filters.bedrooms === opt 
                          ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white shadow-xl' 
                          : 'bg-transparent text-gray-400 border-gray-100 dark:border-zinc-900 hover:border-gray-200 dark:hover:border-zinc-800'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-gray-50 dark:border-zinc-900 pb-2">
                  <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-900 dark:text-white">Price Range</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Min Price</label>
                    <input 
                      type="number" 
                      placeholder="No Min"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-xl text-xs font-bold outline-none"
                      value={filters.minPrice}
                      onChange={e => setFilters({...filters, minPrice: e.target.value ? Number(e.target.value) : ''})}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Max Price</label>
                    <input 
                      type="number" 
                      placeholder="No Max"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-xl text-xs font-bold outline-none"
                      value={filters.maxPrice}
                      onChange={e => setFilters({...filters, maxPrice: e.target.value ? Number(e.target.value) : ''})}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-400">Features</h3>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { key: 'is_furnished', label: 'Fully Furnished' },
                    { key: 'is_serviced', label: 'Serviced Property' },
                    { key: 'is_shared', label: 'Shared Apartment' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => toggleStatus(key as any)}
                      className={`flex items-center justify-between p-4 rounded-2xl transition-all border-2 ${
                        filters[key as keyof FilterState]
                          ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'bg-gray-50 dark:bg-zinc-900/50 border-transparent text-gray-500 dark:text-zinc-500'
                      }`}
                    >
                      <span className="text-[11px] font-black uppercase tracking-widest">{label}</span>
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center border-2 transition-colors ${
                        filters[key as keyof FilterState] ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-200 dark:border-zinc-800'
                      }`}>
                        {filters[key as keyof FilterState] && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-gray-50 dark:border-zinc-900 pb-2">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-900 dark:text-white">Added to Site</h3>
                </div>
                <div className="relative">
                  <select 
                    className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm appearance-none border border-transparent focus:border-gray-200 dark:focus:border-zinc-800"
                    value={filters.timeframe}
                    onChange={e => setFilters({...filters, timeframe: e.target.value})}
                  >
                    {timeframeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 dark:border-zinc-900 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md flex gap-4">
              <button 
                onClick={handleReset}
                className="flex-1 py-4 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Reset All
              </button>
              <button 
                onClick={() => setIsDrawerOpen(false)}
                className="flex-[2] bg-black dark:bg-white text-white dark:text-black py-4 rounded-[20px] font-black text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition-all"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
